import React, { useEffect, useMemo, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { useFormikContext } from 'formik';

import type { ConditionalSectionsContextValue } from '~/components/core/Editor/extensions/ConditionalSection';
import { ConditionalSectionsContext } from '~/components/core/Editor/extensions/ConditionalSection';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import type { TemplateFormSubmitValuesProps, TemplateSectionFormValue } from '~/components/GenericTemplates/types';
import { getTemplateSectionsUrl } from '~/components/GenericTemplates/utils/routes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type { GenericTemplateSectionsResponse } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import ConditionalSectionDialog from './ConditionalSectionDialog';
import { CONDITIONAL_SECTIONS_TEMPLATE_TYPES, stripSectionWrappers } from './sections';

/** Whether the template builder edits conditional sections: the flag is on and the template type takes them. */
const useIsConditionalSectionsEnabled = (templateType: string): boolean => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  return (
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONDITIONAL_TEMPLATE_SECTIONS) &&
    CONDITIONAL_SECTIONS_TEMPLATE_TYPES.includes(templateType)
  );
};

interface ConditionalSectionsProviderProps {
  templateType: string;
}

/**
 * Keeps the form's `sections` list, which holds each section's label and condition, and gives the body
 * editor the chips and the condition dialog. `sections` stays undefined until the stored sections load:
 * the save then omits it, so the backend keeps them, and no condition can be edited.
 */
const ConditionalSectionsProvider: React.FC<React.PropsWithChildren<ConditionalSectionsProviderProps>> = ({
  templateType,
  children,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { values, setFieldValue } = useFormikContext<TemplateFormSubmitValuesProps>();
  const isEnabled = useIsConditionalSectionsEnabled(templateType);
  const [editing, setEditing] = useState<{ key: string } | null>(null);
  const [editorRemountKey, setEditorRemountKey] = useState(0);
  const templateId = values.id;
  const sections = values.sections;

  // On a load error `sections` stays undefined: the save omits it and the stored ones stay as they are.
  // The fetcher reports the error, and its dialog explains why the condition actions are missing.
  const sectionsUrl =
    isEnabled && templateId !== undefined ? getTemplateSectionsUrl(organization.id, templateId) : null;
  const { data: storedSections } = useDataFetcher<GenericTemplateSectionsResponse>(sectionsUrl, null, !!sectionsUrl);

  useEffect(() => {
    if (isEnabled && templateId === undefined) {
      setFieldValue('sections', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, templateId]);

  useEffect(() => {
    if (storedSections) {
      setFieldValue(
        'sections',
        storedSections.sections.map((section) => ({
          ...section,
          // The schema types the tree as a plain object; the rules engine stores a query-builder group.
          conditions_json: section.conditions_json as RuleGroupType,
          isConditionChanged: false,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedSections]);

  // The section rules are scoped to the template's LOBs and sub-organizations, so a scope change
  // removes every condition and keeps the text.
  useFormikChangeListener<TemplateFormSubmitValuesProps>({
    listenForKeys: ['lobs', 'is_all_lobs', 'sub_organization_ids', 'is_all_suborgs'],
    onChange: (newValues) => {
      if (newValues.sections?.length) {
        setFieldValue('sections', []);
        setFieldValue('body_template', stripSectionWrappers(newValues.body_template));
        // The body editor reads its content only when it mounts, so remount the children to show the stripped body.
        setEditorRemountKey((key) => key + 1);
      }
    },
    runOnFirstRender: false,
  });

  const contextValue = useMemo<ConditionalSectionsContextValue>(
    () => ({
      getLabel: (key) => sections?.find((section) => section.key === key)?.label,
      // Whoever may edit the template may edit its sections; until the stored ones load there is nothing to edit.
      canEditConditions: sections !== undefined,
      onCreate: (key) => setEditing({ key }),
      onEdit: (key) => setEditing({ key }),
      onRemove: (key) =>
        setFieldValue(
          'sections',
          sections?.filter((section) => section.key !== key)
        ),
    }),
    [sections, setFieldValue]
  );

  if (!isEnabled) {
    return <>{children}</>;
  }

  const editedSection = editing ? sections?.find((section) => section.key === editing.key) : undefined;

  const saveSection = (label: string, query: RuleGroupType) => {
    if (!editing) {
      return;
    }
    const updated: TemplateSectionFormValue = {
      key: editing.key,
      label,
      conditions_json: query,
      isConditionChanged: true,
    };
    const others = (sections ?? []).filter((section) => section.key !== editing.key);
    setFieldValue('sections', [...others, updated]);
    setEditing(null);
  };

  return (
    <ConditionalSectionsContext.Provider value={contextValue}>
      <React.Fragment key={editorRemountKey}>{children}</React.Fragment>
      {editing ? (
        <ConditionalSectionDialog
          initialLabel={editedSection?.label ?? ''}
          initialQuery={editedSection?.conditions_json ?? null}
          scope={{
            organizationId: organization.id,
            lobs: values.is_all_lobs ? [] : values.lobs,
            isAllLobs: values.is_all_lobs,
            isAllSubOrgs: !organization.sub_organizations_enabled || values.is_all_suborgs,
            subOrgIds: values.sub_organization_ids,
          }}
          onSave={saveSection}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </ConditionalSectionsContext.Provider>
  );
};

export default ConditionalSectionsProvider;
